/**
 * Service for fetching login screen configuration from the backend
 */

import { buildApiUrl } from '../utils/apiConfig';
import { LoginScreenConfig } from '../types/loginScreen';
import { preloadImage as preloadImageUtil } from '../utils/imagePreload';
import {
  cachedLoginScreenToConfig,
  readLoginScreenCache,
  writeLoginScreenCache,
} from '../utils/loginScreenCache';

/**
 * Fetches the login screen configuration from the backend API
 * @returns Promise<LoginScreenConfig | null> - Returns the config or null if not found
 */
export const fetchLoginScreenConfig = async (): Promise<LoginScreenConfig | null> => {
  try {
    const response = await fetch(buildApiUrl('api/v1/login-screen'), {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      // Don't send credentials - this is a public endpoint and sending auth cookies
      // can cause redirect loops if backend redirects authenticated users
    });

    if (response.status === 404) {
      // No configuration found - return null to use defaults
      console.warn('No login screen configuration found, using defaults');
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch login screen config: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching login screen configuration:', error);
    return null;
  }
};

/**
 * Preloads an image to avoid flash of unstyled content
 * @param url - The image URL to preload
 * @returns Promise<boolean> - Returns true if successful, false otherwise
 */
export const preloadImage = preloadImageUtil;

let loginScreenConfigPromise: Promise<LoginScreenConfig | null> | null = null;
export type LoginScreenHeroLoadResult = {
  config: LoginScreenConfig | null;
  heroReady: boolean;
};

let loginScreenHeroReadyPromise: Promise<LoginScreenHeroLoadResult> | null = null;
/** Set after hero bytes decode; LoginPage reads this on mount after Sign In prefetch. */
let primedLoginScreenConfig: LoginScreenConfig | null = null;

function warmLoginHero(config: LoginScreenConfig | null): void {
  if (!config?.heroImageUrl) {
    return;
  }
  void preloadImage(config.heroImageUrl).then((ok) => {
    if (ok) {
      writeLoginScreenCache(config);
      primedLoginScreenConfig = config;
    }
  });
}

/**
 * Returns login-screen JSON as soon as the API responds. Preload runs in the background.
 * Concurrent callers (Sign In prefetch, LoginPage) share one fetch.
 */
export function ensureLoginScreenConfig(): Promise<LoginScreenConfig | null> {
  if (!loginScreenConfigPromise) {
    loginScreenConfigPromise = fetchLoginScreenConfig().then((config) => {
      if (config) {
        writeLoginScreenCache(config);
      }
      warmLoginHero(config);
      return config;
    });
  }
  return loginScreenConfigPromise;
}

/**
 * Config + decoded CMS hero. Call before navigating to /login from marketing chrome.
 */
export function ensureLoginScreenHeroReady(): Promise<LoginScreenHeroLoadResult> {
  if (!loginScreenHeroReadyPromise) {
    loginScreenHeroReadyPromise = (async () => {
      const config = await ensureLoginScreenConfig();
      if (!config?.heroImageUrl) {
        primedLoginScreenConfig = config;
        return { config, heroReady: true };
      }
      const heroReady = await preloadImage(config.heroImageUrl);
      if (heroReady) {
        writeLoginScreenCache(config);
        primedLoginScreenConfig = config;
      }
      return { config, heroReady };
    })().finally(() => {
      loginScreenHeroReadyPromise = null;
    });
  }
  return loginScreenHeroReadyPromise;
}

/** Warm login-screen config and hero bytes (non-blocking). */
export function prefetchLoginScreenHero(): void {
  void ensureLoginScreenConfig().catch(() => {});
}

/** Hero already decoded this session (after ensureLoginScreenHeroReady or successful preload). */
export function getPrimedLoginScreenConfig(): LoginScreenConfig | null {
  if (primedLoginScreenConfig?.heroImageUrl) {
    return primedLoginScreenConfig;
  }
  const cached = readLoginScreenCache();
  return cached ? cachedLoginScreenToConfig(cached) : null;
}

/** @deprecated Use ensureLoginScreenConfig / ensureLoginScreenHeroReady */
export async function loadLoginScreenConfigWithHero(): Promise<{
  config: LoginScreenConfig | null;
  heroReady: boolean;
}> {
  return ensureLoginScreenHeroReady();
}
